import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", message: "", company: "" };

export default function EstimateForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/estimate`, form);
      if (!res.data?.ok) throw new Error("bad response");
      toast.success("Request sent — we'll get back to you as soon as possible.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please call us at (406) 291-6639.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="estimate-form" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="est-name" className="block text-xs font-mono font-bold uppercase tracking-wider text-slate-400 mb-2">
            Name
          </label>
          <input
            id="est-name"
            type="text"
            required
            minLength={2}
            maxLength={100}
            value={form.name}
            onChange={set("name")}
            data-testid="estimate-name-input"
            placeholder="Your name"
            className="input-dark"
          />
        </div>
        <div>
          <label htmlFor="est-phone" className="block text-xs font-mono font-bold uppercase tracking-wider text-slate-400 mb-2">
            Phone
          </label>
          <input
            id="est-phone"
            type="tel"
            required
            maxLength={30}
            value={form.phone}
            onChange={set("phone")}
            data-testid="estimate-phone-input"
            placeholder="(406) 000-0000"
            className="input-dark"
          />
        </div>
      </div>
      <div>
        <label htmlFor="est-email" className="block text-xs font-mono font-bold uppercase tracking-wider text-slate-400 mb-2">
          Email
        </label>
        <input
          id="est-email"
          type="email"
          required
          maxLength={254}
          value={form.email}
          onChange={set("email")}
          data-testid="estimate-email-input"
          placeholder="you@example.com"
          className="input-dark"
        />
      </div>
      <div>
        <label htmlFor="est-message" className="block text-xs font-mono font-bold uppercase tracking-wider text-slate-400 mb-2">
          How can we help?
        </label>
        <textarea
          id="est-message"
          rows={5}
          maxLength={5000}
          value={form.message}
          onChange={set("message")}
          data-testid="estimate-message-input"
          placeholder="Tell us about your project — what needs welding, repairing, or fabricating, and where you're located."
          className="input-dark resize-y"
        />
      </div>
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        value={form.company}
        onChange={set("company")}
        aria-hidden="true"
        className="hidden"
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="estimate-submit-button"
        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors disabled:opacity-60"
      >
        {sending ? "Sending…" : "Request a Free Estimate"}
        {!sending && <ArrowRight size={16} aria-hidden="true" />}
      </button>
    </form>
  );
}
